// src/components/Auth/Auth.js

import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import loadingStyles from './../../styles/Common.module.css';

// 재시도 로직을 위한 유틸리티 함수
const fetchWithRetry = async (url, options, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options);
      // 500번대 서버 에러일 경우 에러를 던져서 재시도 유도
      if (res.status >= 500) {
        throw new Error(`Server Error: ${res.status}`);
      }
      return res; // 200, 400, 404 등은 여기서 반환됨
    } catch (err) {
      if (i === retries - 1) throw err; // 마지막 시도에서도 실패하면 에러 throw
      await new Promise(resolve => setTimeout(resolve, delay)); // 대기 후 재시도
    }
  }
};

function RequireAuth({ children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [checking, setChecking] = useState(true);
  const [hasNickname, setHasNickname] = useState(false);
  const [serverError, setServerError] = useState(false); // 서버 에러 상태 추가

  useEffect(() => {
    // 1. 로딩 중
    if (auth.isLoading) return;

    // 2. 미인증 상태
    if (auth.error || !auth.isAuthenticated) {
      sessionStorage.setItem('redirect_after_login', location.pathname);
      navigate("/signin", { state: { from: location.pathname } });
      return;
    }

    const userId = auth.user?.profile?.sub; // 사용자 고유 ID (sub)
    const storageKey = `nickname_checked_${userId}`; // 사용자별로 키 분리

    // 1. 로컬 스토리지 캐시 확인 (강력한 캐싱)
    if (localStorage.getItem(storageKey) === 'true') {
      setHasNickname(true);
      setChecking(false);
      return; 
    }

    // 3. 닉네임 확인 로직
    const checkNickname = async () => {
      // 이미 세션에 확인된 기록이 있다면 API 호출 스킵 (선택 사항 - 성능 최적화)
      if (sessionStorage.getItem('nickname_checked') === 'true') {
        setHasNickname(true);
        setChecking(false);
        return;
      }

      try {
        // 재시도 로직이 적용된 fetch 호출
        const res = await fetchWithRetry(
          `${process.env.REACT_APP_API_BASE_URL}/user/nickname/me`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${auth.user.id_token}`,
            },
          }
        );

        if (res.status === 200) {
          const { nickname } = await res.json();
          if (nickname) {
            setHasNickname(true);
            // ✅ 성공 시 로컬 스토리지에 영구 저장 (로그아웃 시 삭제 필요)
            localStorage.setItem(storageKey, 'true'); 
          } else {
            // 200이지만 내용이 비어있는 경우 (백엔드 로직에 따라 다름)
            setHasNickname(false); 
          }
        } else if (res.status === 404) {
          // 명확하게 "닉네임이 없음"인 경우 (백엔드가 없을 때 404를 준다고 가정)
          // 만약 백엔드가 없을 때 200에 nickname: null을 준다면 위 200 블록에서 처리됨
          setHasNickname(false);
        } else {
          // 401, 403 등 권한 문제 혹은 알 수 없는 상태
          console.error(t('auth.errorCheckingNickname'), res.status);
          setServerError(true); // 에러 화면을 보여주기 위해 상태 변경
        }
      } catch (err) {
        // 네트워크 에러 혹은 재시도 횟수 초과
        console.error(t('auth.errorCheckingNickname'), err);
        setServerError(true); // 닉네임 설정 페이지로 보내지 않고 에러 처리
      } finally {
        setChecking(false);
      }
    };

    if (auth.user) {
      checkNickname();
    } else {
      setChecking(false);
    }
  }, [auth, navigate, location, t]);

  if (auth.isLoading || checking) {
    return (
      <div className={loadingStyles.loadingContainer}>
        <div className={loadingStyles.spinner}></div>
        <p>{t('auth.loading')}</p>
      </div>
    );
  }

  // 서버/네트워크 에러가 발생했을 때: 닉네임 설정 페이지로 보내지 말고 재시도 버튼 등을 노출
  if (serverError) {
     return (
      <div className={loadingStyles.loadingContainer}>
        <p>{t('auth.errorNetwork') || "서버와 통신이 원활하지 않습니다."}</p>
        <button onClick={() => window.location.reload()}>
            {t('auth.retry') || "다시 시도"}
        </button>
      </div>
    );
  }

  // 명확하게 닉네임이 없는 경우에만 이동
  if (!hasNickname && !checking) {
    // 무한 리다이렉트 방지: 이미 설정 페이지라면 이동하지 않음
    if (location.pathname !== "/set-nickname") {
        sessionStorage.setItem('redirect_after_login', location.pathname);
        // replace: true를 사용하여 뒤로가기 시 루프 방지
        navigate("/set-nickname", { state: { from: location.pathname }, replace: true });
    }
    // 여기서 null을 리턴하면 컴포넌트가 렌더링되지 않음.
    // SetNicknamePage가 라우터 상 RequireAuth 내부에 있다면 children을 렌더링 해야 할 수도 있음.
    // 구조상 RequireAuth가 감싸고 있다면 null 리턴이 맞습니다.
    return null; 
  }

  return <>{children}</>;
}

export default RequireAuth;